import { Module } from 'vuex'
import { StateInterface } from '@/store'
import { Cookies, LocalStorage } from 'quasar'
import { HostModel, UserModel } from '@/models/auth'
import { DEFAULT_TAB_OPTIONS, ItemViewMode, USER_SETTINGS_CNAMES } from '@/constants'
import { UUID } from '@Protium/vue-ozone-utils'
import { User } from '@fusionauth/typescript-client'

const UserCookieOptions = {
  secure: process.env.NODE_ENV === 'production' // Helps to mitigate XSS attacks
  // expires: '1h' // Expires in 1 hour
}

const HNAMES = {
  __DEV_REFRESH_TOKEN: 'x-devonly-ozone-refresh-token',
  REFRESH_TOKEN: 'x-ozone-refresh-token',
  TOKEN: 'x-ozone-token'
}
const CNAMES = {
  REFRESH_TOKEN: 'ozn__refresh_token',
  TOKEN: 'ozn__token'
}

export interface UserState {
  shouldResetServices: boolean;
  accessToken: string;
  authType: string;
  faAppId: string;
  host: HostModel;
  tenant: string;
  rftoken: string;
  token: string;
  user: Partial<User> & UserModel;
  userDict: Record<UUID, Partial<UserModel>>;
  lastActiveUser: UserModel;
  settings: Record<string, any>;
  roles: Array<string>;
  shouldGenerateRoutes: boolean;
}

const UserModule: Module<UserState, StateInterface> = {
  namespaced: true,
  state: {
    shouldResetServices: true, // NOTE defaults to true so it triggers on page laod
    accessToken: '',
    authType: '',
    faAppId: LocalStorage.getItem('faAppId') || '',
    host: LocalStorage.getItem('host') || '',
    tenant: LocalStorage.getItem('tenant') || '',
    token: Cookies.get(CNAMES.TOKEN) || '',
    rftoken: Cookies.get(CNAMES.REFRESH_TOKEN) || '',
    user: LocalStorage.getItem('user') || '',
    userDict: {},
    lastActiveUser: '',
    roles: [],
    shouldGenerateRoutes: true,
    settings: {
      hideOperationalDashboard: LocalStorage.getItem(USER_SETTINGS_CNAMES.hideOperationalDashboard) === 't' ? true : false,
      defaultTab: LocalStorage.getItem(USER_SETTINGS_CNAMES.defaultTab) || DEFAULT_TAB_OPTIONS[0],
      darkModeEnabled: LocalStorage.getItem(USER_SETTINGS_CNAMES.darkModeEnabled) === 't' ? true : false,
      hideResolved: LocalStorage.getItem(USER_SETTINGS_CNAMES.hideResolved) === 't' ? true : false,
      newFirst: LocalStorage.getItem(USER_SETTINGS_CNAMES.newFirst) === 't' ? true : false,
      orderViewMode: LocalStorage.getItem(USER_SETTINGS_CNAMES.orderViewMode) || ItemViewMode.Default,
      orderAutoAdvance: LocalStorage.getItem(USER_SETTINGS_CNAMES.orderAutoAdvance) === 't' ? true : false,
      showNewAsPickable: LocalStorage.getItem(USER_SETTINGS_CNAMES.showNewAsPickable) === 't' ? true : false,
      showNewAsPackable: LocalStorage.getItem(USER_SETTINGS_CNAMES.showNewAsPackable) === 't' ? true : false,
      scanSerialAsPackable: LocalStorage.getItem(USER_SETTINGS_CNAMES.scanSerialAsPackable) === 't' ? true : false,
      orderTagFilters: (LocalStorage.getItem(USER_SETTINGS_CNAMES.orderTagFilters) || []),

      packFilterCatalogues: (LocalStorage.getItem(USER_SETTINGS_CNAMES.packFilterCatalogues) || []),
      packFilterTags: (LocalStorage.getItem(USER_SETTINGS_CNAMES.packFilterTags) || []),

      // last viewed timestamps
      supplierCatalogueLastViewedTimestamps: (LocalStorage.getItem(USER_SETTINGS_CNAMES.supplierCatalogueLastViewedTimestamps) || {})
    }
  } as UserState,
  mutations: {
    SetSettingKey (state: UserState, { key, value }) {
      if (!state.settings) state.settings = {}
      state.settings[key] = value
    },
    SetOrderTagFilters (state: UserState, value) {
      if (!state.settings) state.settings = {}
      state.settings.orderTagFilters = value
    },
    SetResetServices (state: UserState, value: boolean) {
      state.shouldResetServices = value
    },
    SetAccessToken: (state: UserState, token: string) => {
      state.accessToken = token
    },
    SetAuthType: (state: UserState, type: string) => {
      state.authType = type
    },
    SetFaAppId: (state: UserState, faAppId: string) => {
      state.faAppId = faAppId
      // LocalStorage.set('faAppId', faAppId, UserCookieOptions)
      LocalStorage.set('faAppId', faAppId)
    },
    SetHost: (state: UserState, host) => {
      state.host = host
      // LocalStorage.set('host', host, UserCookieOptions)
      LocalStorage.set('host', host)
    },
    SetTenant: (state: UserState, tenant: string) => {
      state.tenant = tenant
      // LocalStorage.set('tenant', tenant, UserCookieOptions)
      LocalStorage.set('tenant', tenant)
    },
    SetToken: (state: UserState, token: string) => {
      state.token = token
      // LocalStorage.set(CNAMES.TOKEN, token, UserCookieOptions)
      Cookies.set(CNAMES.TOKEN, token)
    },
    SetRfToken: (state: UserState, token: string) => {
      state.rftoken = token
      // LocalStorage.set(CNAMES.TOKEN, token, UserCookieOptions)
      Cookies.set(CNAMES.REFRESH_TOKEN, token)
    },
    SetUser: (state: UserState, user) => {
      state.user = user
      // LocalStorage.set('user', user, UserCookieOptions)
      LocalStorage.set('user', user)
    },
    SetUserDict: (state, users: Record<UUID, Partial<UserModel>>) => {
      state.userDict = { ...(state.userDict || {}), ...(users || {}) }
    },
    SetLastActiveUser: (state: UserState, user) => {
      state.lastActiveUser = user
    },
    SetRoles: (state: UserState, rolesData: Array<string> | { roles: Array<string> }) => {
      const roles: Array<string> = !Array.isArray(rolesData) && rolesData.roles ? (rolesData.roles || []) : (rolesData || []) as Array<string>
      if (state.roles.length <= 0 && roles.length > 0 || state.roles.length > 0 && state.roles.length !== roles.length && !roles.every(r => state.roles.includes(r))) state.shouldGenerateRoutes = true

      state.roles = roles
    },
    SetShouldGenerateRoutes: (state: UserState, value) => {
      state.shouldGenerateRoutes = value
    },
    ClearLastActiveUser: (state: UserState) => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      state.lastActiveUser = ''
    }
  },
  getters: {
    AccessToken: state => state.accessToken,
    AuthType: state => state.authType,
    FaAppId: state => state.faAppId,
    Host: state => state.host,
    Tenant: state => state.tenant,
    Token: state => state.token,
    HasToken: state => !!state.host,
    User: state => state.user,
    ScopeData: state => state.user?.data,
    UserDict: state => state.userDict,
    Roles: state => state.roles,
    LastActiveUser: state => state.lastActiveUser,
    Settings: state => state.settings,
    OrderTagFilters: state => state.settings?.orderTagFilters,
    ShouldGenerateRoutes: state => state.shouldGenerateRoutes
  },
  actions: {
    UpdateUser ({ commit }, { user }) {
      commit('SetUser', user)
    },
    UpdateSetting ({ commit }, { key, value }) {
      commit('SetSettingKey', { key, value })
    },
    AddUserTagFilters ({ state, commit }, tag) {
      const current = (state.settings.orderTagFilters || [])
      const value = current.concat(tag).filter((f, i, a) => a.indexOf(f) === i)

      LocalStorage.set(USER_SETTINGS_CNAMES.orderTagFilters, value)

      commit('SetOrderTagFilters', value)
    },
    RemoveUserTagFilters ({ state, commit }, tag) {
      const current = (state.settings.orderTagFilters || [])
      const index = current.indexOf(tag)
      if (index < 0) return
      current.splice(index, 1)

      const value = current.filter((f, i, a) => a.indexOf(f) === i)
      LocalStorage.set(USER_SETTINGS_CNAMES.orderTagFilters, value)

      commit('SetOrderTagFilters', value)
    },
    SetApplication ({ commit, dispatch }, { application, host }) {
      return new Promise(resolve => {
        // console.log('[User/SetApplication]: ', application, host)
        if (!host || !application) {
          resolve(null)
          return
        }
        commit('SetHost', host)
        commit('SetFaAppId', application.id)

        dispatch('ResetServices', { host, application: application.id }).catch(console.error)

        resolve([host, application.id])
      })
    },
    SetData ({ commit }, { user, tenantId }) {
      return new Promise(resolve => {
        // console.log('[User/SetData]: ', user, tenantId)
        if (!user) {
          resolve(null)
          return
        }
        commit('SetUser', user)
        commit('SetTenant', tenantId)
        resolve(user)
      })
    },
    ResetServices ({ dispatch, commit }, { user, host, application, token, rftoken }) {
      try {
        // console.log('[User/ResetServices]', host.url, application)

        // NOTE always uses "auth proxy" on dev, so we can set it to target the local dev server directly
        // otherwise in production, fallback to to proxy only when host is not yet known
        // const url = process.env.NODE_ENV === 'development'
        //   ? process.env.OZONE_AUTH_PROXY_URL
        //   : state.host ? state.host.url : process.env.OZONE_AUTH_PROXY_URL

        dispatch('Services/Generate', [
          { name: 'Directr', props: { user, host, application, token, rftoken, io: true }, set: true },
          { name: 'Sys', props: { user, host, application, token, rftoken }, set: true },
          { name: 'Cdn', props: { user, host, application, token, rftoken }, set: true }
        ], { root: true })
          .then(done => {
            commit('SetResetServices', false)
          })
          .catch(e => {
            throw Error(e as string)
          })

        // dispatch('Services/SetIOHost', { host }).catch(console.error)
      } catch (e) {
        return e
      }
    },
    GeneratedRoutes ({ commit }) {
      commit('SetShouldGenerateRoutes', false)
    },
    HandleAuthorization ({ state, commit }, headers) {
      // store token to memory
      commit('SetToken', (headers || {})[HNAMES.TOKEN] || Cookies.get(CNAMES.TOKEN))
      commit('SetRfToken', (headers || {})[HNAMES.REFRESH_TOKEN] || Cookies.get(CNAMES.REFRESH_TOKEN))
    },
    CheckAuthentication ({ state, commit, dispatch }) {
      const authStatus = (
        LocalStorage.getItem('faAppId') !== null &&
        LocalStorage.getItem('host') !== null &&
        LocalStorage.getItem('tenant') !== null &&
        Cookies.get(CNAMES.TOKEN) !== null
      )

      // console.log('auth checkpoints', LocalStorage.getItem('faAppId'), LocalStorage.getItem('host'), LocalStorage.getItem('tenant'), Cookies.get(CNAMES.TOKEN))
      // console.log('check auth...', authStatus)

      // COMBAK should only reset on change, since this is called on each route change
      if (authStatus && state.shouldResetServices) {
        // console.log('resetting services...')

        const user: UserModel | null = LocalStorage.getItem('user')
        const appId = LocalStorage.getItem('faAppId')
        const host = LocalStorage.getItem('host')

        const appRoles = user?.registrations?.find(r => r.applicationId === appId)?.roles || []
        const roles = appRoles.every(r => typeof r === 'string')
          ? appRoles
          : appRoles.roles.map(r => r.name)

        commit('SetRoles', roles)

        dispatch('ResetServices', { user, host, application: appId, token: state.token, rftoken: state.rftoken })
          .then(done => {
            commit('SetResetServices', false) // just in case...
          }).catch(console.error)

        if (!Object.keys(state.userDict || {}).length) void dispatch('FetchUserList')
      }

      return authStatus
    },
    async Auth ({ commit, dispatch }, { type, userData, headers }) {
      try {
        if (!userData?.user) throw Error('Login Failed')
        if (!userData?.host) throw Error('Missing Hostname')
        // if (!userData?.application || !userData?.tenantId || !userData?.token) throw Error('Missing required auth data')

        const { user, host, application, tenantId, token } = userData
        if (!user || !host || !application) {
          console.error('Auth Error: Invalid data', userData)
          return Promise.reject('API Returned invalid data')
        }

        // TODO deprecate storing token in store (cookie is enough)
        dispatch('HandleAuthorization', headers).catch(console.warn)

        commit('SetAuthType', type)
        commit('SetUser', user)

        const userRoles = application.roles.every(r => typeof r === 'string')
          ? application.roles
          : application.roles.map(r => r.name)
        const roles = { roles: userRoles, nodes: [] }
        commit('SetRoles', roles)


        await dispatch('SetApplication', { application, host })
        await dispatch('SetData', { user, token, tenantId })

        // host might have changed, services should reset
        commit('SetResetServices', true)

        return Promise.resolve(roles)
      } catch (error) {
        return Promise.reject(error)
      }
    },
    async Login ({ commit, state, rootGetters }, { user, pass, tenant }) {
      try {
        if (!rootGetters['Services/Sys']) throw Error('Cannot login. Auth service is not initialized')
        return (await rootGetters['Services/Sys'].login(user, pass, { tenant, method: 'password' }))
      } catch (e) {
        return e
      }
    },
    async Register ({ commit, state, rootGetters }, { user, pass, data, tenant }) {
      try {
        if (!rootGetters['Services/Sys']) throw Error('Cannot register. Auth service is not initialized')
        return (await rootGetters['Services/Sys'].register(user, pass, data, { tenant }))
      } catch (e) {
        return e
      }
    },
    Logout ({ commit, rootGetters, state }) {
      if (!rootGetters['Services/Sys']) return Promise.reject('Cannot Logout. Auth service is not initialized')
      return new Promise((resolve, reject) => {
        if (!state.host) {
          resolve(null)
          return
        }
        const clear = () => {
          commit('SetLastActiveUser', state.user)

          commit('SetAccessToken', '')
          commit('SetAuthType', '')
          commit('SetFaAppId', '')
          commit('SetHost', {})
          commit('SetTenant', '')
          commit('SetToken', '')
          commit('SetUser', {})
          commit('SetRoles', [])

          LocalStorage.remove('faAppId')
          LocalStorage.remove('host')
          LocalStorage.remove('tenant')
          Cookies.remove(CNAMES.TOKEN)
          LocalStorage.remove('user')
        }

        rootGetters['Services/Sys'].logout(state.user.id)
          .then(() => {
            clear()
            window.location.replace(window.location.origin)
            resolve('')
          })
          .catch(e => {
            clear()
            window.location.replace(window.location.origin)
            console.error('[User/Logout] Error logging out', e)
            reject(e)
          })
      })
    },
    async FetchUserList ({ commit, rootGetters }) {
      try {
        // console.log('fetching user list...')
        const users = await rootGetters['Services/Sys'].get('auth/list')
          .then(({ items }) => {
            return Object.fromEntries(items.map(({ id, firstName, lastName, fullName }) => ([id, { id, label: fullName, firstName, lastName, fullName }])) as Array<any>)
          })

        commit('SetUserDict', users)
      } catch (e) {
        console.warn('Failed to load user list -', e)
      }
    }
  }
}

export default UserModule
